import { type MouseEvent as ReactMouseEvent, useState } from 'react';

import { DownloadDataButton } from './DownloadDataButton.js';
import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { A11yToolbarTabIndex } from '../../../core/types/toolbar.js';
import { zIndicesCategoricalAreas } from '../../constants.js';
import { useMouseInBoundsState } from '../../hooks/store/selectors.js';
import { useCategoricalBarChartBaseConfig } from '../../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartRawData } from '../../hooks/useCategoricalBarChartRawData.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

type CategoricalBarChartToolbarProps = {
  onMouseEnter: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export const CategoricalBarChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
}: CategoricalBarChartToolbarProps) => {
  const [isTooltipHidden, setIsTooltipHidden] = useState(false);
  const rawData = useCategoricalBarChartRawData();
  const { toolbar, intents } = useCategoricalBarChartBaseConfig();
  const isInChartBounds = useMouseInBoundsState();

  const isDownloadEnabled = !!toolbar.downloadData?.enabled;
  const behaviorTrackingProps = _useBehavioralTrackingProps(toolbar)[0];

  if (toolbar.hidden || !(isDownloadEnabled || intents.length)) {
    return null;
  }

  const handleToggleMenu = (open: boolean): void => {
    !open && onMouseLeave();
  };

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(rawData);

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents.length;
  const showMenuToolbarButton = isDownloadEnabled && !!intents.length;
  const initialA11yState: A11yToolbarTabIndex[] =
    showDefaultToolbarDownloadButton ? [{ download: 0 }] : [{ intent: 0 }];

  return (
    <ChartToolbarRenderer
      visible={isInChartBounds}
      placement={toolbar.placement}
      collapsed={toolbar.collapsed}
      draggable={true}
      zIndex={zIndicesCategoricalAreas.toolbar}
      initialA11yState={initialA11yState}
      onToggleToolbar={setIsTooltipHidden}
      onToggleMenu={handleToggleMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuDisplayMode="never"
      intents={intents}
      isDownloadEnabled={showMenuToolbarButton}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      removeDragHandle
      {...behaviorTrackingProps}
    >
      {showDefaultToolbarDownloadButton && (
        <DownloadDataButton hideTooltip={isTooltipHidden} />
      )}
    </ChartToolbarRenderer>
  );
};
